import React, { useEffect, useRef, useState, forwardRef, useContext } from "react";
import { AutomatizationStep, StandardProduct } from "../types/cartAutomatizationStatus";


import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,    
} from "@/components/ui/sheet";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"

import {
    Item,
    ItemActions,
    ItemContent,
    ItemHeader,
    ItemMedia,
    ItemTitle,
} from "@/components/ui/item"

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel"


import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";


import { cn } from "@/lib/utils";
import { AppDataContext } from "modules/main/context/appData";
import { get } from "http";
import { getStoreBdData } from "utils/storeDataManager";
import { useFetch } from "modules/main/hooks/useFetch";
import { FetchDataOptions } from "modules/main/services/fetchData";
import { addReplacedItems, getMissedItemsEndpoint } from "modules/main/services/endpoints";
import { MinimalProductData, MissedProduct, MissedProductsResponse } from "../types/missedProductResponse";
import { Info, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import Amount from "utils/amount";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";



interface CartCreationMissedItemsProps extends React.HTMLAttributes<HTMLDivElement> {
    scenarioId:number;
    status: AutomatizationStep[];
    openState:boolean;
    setOpen: (open: boolean) => void;
    refreshTik: number;
}

export const CartCreationMissedItems = forwardRef<HTMLDivElement, CartCreationMissedItemsProps>(({className,scenarioId,status,refreshTik,...props},ref) => {
    const {storeList} = useContext(AppDataContext); 
    const isMobile = !useMediaQuery(isDesktopSize);
    const missedItemSkus = status.reduce((acc: string[], current) => {
        if(current.results && current.results.removed.length > 0){
            acc.push(...current.results.removed.map(product => product.hashed_sku));
        }
        return acc;
    },[]);
    const shouldRefresh = refreshTik % 2 === 0;
    const requestOptions:FetchDataOptions = {
        method:"GET",
        headers:{"content-type":"application/json"},
    }
    const {data,error,loading} = useFetch<MissedProductsResponse>(getMissedItemsEndpoint(missedItemSkus),requestOptions,shouldRefresh);

    // Estado para las selecciones de alternativas
    // key: hashed_sku del producto removido, value: hashed_sku de la alternativa seleccionada
    const [selectedAlternatives, setSelectedAlternatives] = useState<Record<string, string>>({});

    // Marcar el primer elemento por defecto cuando se obtienen los productos perdidos
    useEffect(() => {
        if (data?.products) {
            setSelectedAlternatives(prev => {
                const newSelections = { ...prev };
                data.products.forEach(product => {
                    // Si no hay alternativa seleccionada, marcar "no reemplazar" por defecto
                    if (!newSelections[product.hashed_sku]) {
                        newSelections[product.hashed_sku] = "no_replace";
                    }
                });
                return newSelections;
            });
        }
    }, [data?.products]);

    if (missedItemSkus.length === 0) return null;

    const handleClose = (open: boolean) => {
        props.setOpen(open);
    }

    // Handler para cambiar la alternativa seleccionada de un producto
    const handleAlternativeChange = (productSku: string, alternativeSku: string) => {
        setSelectedAlternatives(prev => ({ ...prev, [productSku]: alternativeSku }));
    };

    // Handler para confirmar cambios (enviar al backend)
    const handleConfirm = () => {
        console.log("Selecciones a enviar:", selectedAlternatives);

        const items:{store_id:number, hashed_sku:string,quantity:number}[] = [];
        
        // Recorrer el status para encontrar la tienda y cantidad correspondiente a la alternativa seleccionada
        status.forEach(step => {
            if (step.results && step.results.removed.length > 0) {
                step.results.removed.forEach(removedProduct => {
                    const alternativeSku = selectedAlternatives[removedProduct.hashed_sku];
                    // Si el usuario tiene una alternativa seleccionada (y no es "no reemplazar")
                    if (alternativeSku && alternativeSku !== "no_replace") {
                        items.push({
                            store_id: step.store_id,
                            hashed_sku: alternativeSku,
                            quantity: removedProduct.quantity // Mantenemos la cantidad original solicitada
                        });
                    }
                });
            }
        });

        //crear fetch para enviar las selecciones al backend y actualizar el carrito
        fetch(addReplacedItems, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                scenario_id: scenarioId, 
                items: items
                
            }),
            credentials: "include"
        }).then(response => {
            if (!response.ok) {
                throw new Error("Failed to update missed items");
            }
            // Cerrar el diálogo después de confirmar
            props.setOpen(false);
        }).catch(error => {
            console.error("Error updating missed items:", error);
        });
        handleClose(false);

    };

    const content = (
        <>
            <div className="w-full">
                <ScrollArea
                    type="scroll"
                    className={cn(
                        "h-full w-full flex-1",
                        isMobile
                            ? "[&>[data-radix-scroll-area-viewport]]:max-h-[calc(100dvh-300px)]"
                            : "[&>[data-radix-scroll-area-viewport]]:max-h-[65dvh]"
                    )}
                >
                    <div className="flex flex-col gap-4 py-4 px-0">
                        {status.filter((s) => {
                            if(!s.results)
                                return false;
                            return s.results.removed.length > 0;
                            }).map((store, index) => {
                                const storeInfo = getStoreBdData(store.store_id,storeList);
                                return <MissedProductStore 
                                    key={index} 
                                    store_id={store.store_id} 
                                    logo_url={storeInfo?.logo_url ?? ""}
                                    name={storeInfo?.name ?? ""} 
                                    products_status={store.results?.removed ?? []}
                                    products={data?.products.filter(product => store.results?.removed.some((removed) => removed.hashed_sku === product.hashed_sku)) ?? []}
                                    selectedAlternatives={selectedAlternatives}
                                    onAlternativeChange={handleAlternativeChange}
                                />
                            })
                        }
                    </div>
                </ScrollArea>
            </div>
        </>
    );

    return (
    <div className={cn("", className)} ref={ref}>
        {isMobile ? (
            <Sheet open={props.openState} onOpenChange={(open) => handleClose(open)}>
                <SheetContent side={"bottom"} className="h-[95dvh]">
                    <SheetHeader>
                        <SheetTitle>Algunos productos no están disponibles</SheetTitle>
                        <SheetDescription>No pudimos agregar los siguientes productos a tu carrito</SheetDescription>
                    </SheetHeader>
                    {content}
                    <SheetFooter className="mt-4">
                        <Button
                            variant="default"
                            onClick={handleConfirm}
                            disabled={!data || data.products.some(p => !p.isReady)}
                        >
                            Confirmar cambios
                        </Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>
        ) : (
            <Dialog open={props.openState} onOpenChange={(open) => handleClose(open)}>
                <DialogContent className="max-w-5xl max-h-[90dvh]">
                    <DialogHeader>
                        <DialogTitle>Algunos productos no están disponibles</DialogTitle>
                        <DialogDescription>No pudimos agregar los siguientes productos a tu carrito</DialogDescription>
                    </DialogHeader>
                    {content}
                    <DialogFooter className="mt-2">
                        <Button
                            variant="default"
                            onClick={handleConfirm}
                            disabled={!data || data.products.some(p => !p.isReady)}
                        >
                            Confirmar cambios
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        )}
    </div>
    );
});


interface MissedProductStoreProps extends React.HTMLAttributes<HTMLDivElement>{
    store_id:number;
    logo_url:string;
    name:string;
    products_status:StandardProduct[];
    products: MissedProduct[];
    selectedAlternatives: Record<string, string>;
    onAlternativeChange: (productSku: string, alternativeSku: string) => void;
}

const MissedProductStore = forwardRef<HTMLDivElement, MissedProductStoreProps>(({store_id, products, selectedAlternatives, onAlternativeChange, ...props},ref) => {
    return(
    <Card className="gap-0 py-4">
        <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
                <Avatar className="size-9">
                    <AvatarImage src={props.logo_url}/>
                    <AvatarFallback>ER</AvatarFallback>
                </Avatar>
                <span>{props.name}</span>
            </CardTitle>
            {/* <CardAction></CardAction> */}
        </CardHeader>
        <CardContent className="px-4 mt-0 flex flex-col gap-2">
            {products.map((product,index) => {
                const requestedProduct = props.products_status.find(p => p.hashed_sku === product.hashed_sku);
                return <React.Fragment key={product.hashed_sku}>
                        <MissedProductItem 
                            product={product} 
                            requested_product={requestedProduct!}
                            selectedAlternative={selectedAlternatives[product.hashed_sku] || product.hashed_sku}
                            onAlternativeChange={onAlternativeChange}
                        />
                        {index !== products.length - 1 && <Separator className=""/>}
                        </React.Fragment>
            })
            }
        </CardContent>            
    </Card>
    );
});

interface MissedProductItemProps extends React.HTMLAttributes<HTMLDivElement>{
    product:MissedProduct;
    requested_product:StandardProduct;
    selectedAlternative: string;
    onAlternativeChange: (productSku: string, alternativeSku: string) => void;
}

const MissedProductItem = forwardRef<HTMLDivElement, MissedProductItemProps>(({product, requested_product, selectedAlternative, onAlternativeChange, ...props},ref) => {
    // Las alternativas pueden venir en product.alternatives o solo el producto mismo si no hay alternativas
    const alternatives = product.alternatives && product.alternatives.length > 0
        ? product.alternatives
        : [];
    const imageUrl = product.images?.catalog??product.image_url;
    return(
        <Item variant={"default"}>
            <ItemMedia variant="image" className="opacity-60">
                <img className="Image-80" src={imageUrl} alt={product.name}/>
            </ItemMedia>
            <ItemContent className="opacity-60">
                <ItemTitle className="flex flex-col gap-1 w-full items-start">
                    <span>{product.name}</span>
                    <div className="flex flex-row w-full justify-between">
                        <div className="flex flex-col gap-1">
                            <div className="flex gap-2 items-center">
                                <span className="text-xs text-muted-foreground">{requested_product.quantity} un. solicitadas</span>
                                <span className="text-white"><Badge variant="destructive" className="text-xxs">Sin Stock</Badge></span>
                            </div>                            
                            <div className="flex gap-2 text-xxs text-primary font-semibold"><Info className="w-4 h-4"/>Opciones disponibles</div>
                        </div>
                        <div>
                            <Amount className="text-sm font-semibold" amount={product.price} currency={"CLP"} locale="es-CL" />
                        </div>
                    </div>
                </ItemTitle>
            </ItemContent>
            <ItemHeader>         
                {
                (product.isReady) ?
                    (
                        alternatives.length > 0 ? (
                            <MissedProductAlternativesCarousel 
                                alternatives={alternatives}
                                selectedAlternative={selectedAlternative}
                                onAlternativeChange={(altSku) => onAlternativeChange(product.hashed_sku, altSku)}
                            />
                        ) : (
                            <div className="text-xs text-muted-foreground">No hay alternativas disponibles</div>
                        )
                    ) : (
                        <div className="w-full max-w-[20rem] overflow-hidden mt-2">
                            <div className="flex flex-row gap-3 pl-1">
                                {[1,2].map(i => (
                                    <div key={i} className="flex flex-col gap-2 min-w-[12rem] max-w-[12rem] shrink-0 p-2 border border-muted rounded-xl">
                                        <Skeleton className="w-full h-20 rounded-lg" />
                                        <Skeleton className="w-3/4 h-3 rounded" />
                                        <Skeleton className="w-1/2 h-3 rounded" />
                                        <Skeleton className="w-1/3 h-4 rounded" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )
                }
            </ItemHeader>
            {/* <ItemActions>
                
            </ItemActions> */}
        </Item>
    );
});


interface MissedProductAlternativesCarouselProps {
    alternatives: MinimalProductData[];
    selectedAlternative: string;
    onAlternativeChange: (alternativeSku: string) => void;
}

const MissedProductAlternativesCarousel = forwardRef<HTMLDivElement, MissedProductAlternativesCarouselProps>(({alternatives, selectedAlternative, onAlternativeChange, ...props},ref) =>{
    return(
    <Carousel
        opts={{
        align: "start",
        }}
        className="w-full max-w-sm"
    ><div className="flex flex-col">
        <RadioGroup value={selectedAlternative} onValueChange={onAlternativeChange}>
        <CarouselContent>
            {/* Opción: No reemplazar */}
            <CarouselItem key="no_replace" className="basis-1/2 md:basis-1/2 lg:basis-1/2 min-w-[12rem] min-h[15rem] mx-2">
            <div className="p-1 min-w-[12rem]">
                <Item
                    variant={"outline"}
                    className={`max-w-[12rem] min-w-[12rem] min-h-[15rem] cursor-pointer ${
                        selectedAlternative === "no_replace" ? "border-2 border-primary" : "border border-muted"
                    }`}
                    onClick={() => onAlternativeChange("no_replace")}
                >
                    <ItemHeader>
                        <div className="flex items-center justify-center h-20">
                            <Ban className="w-10 h-10 text-muted-foreground/40" />
                        </div>
                    </ItemHeader>
                    <ItemContent>
                        <ItemTitle className="flex flex-col gap-1 w-full items-start text-xs">
                            <span className="font-medium text-muted-foreground">No reemplazar</span>
                            <span className="text-muted-foreground/60">Omitir este producto</span>
                        </ItemTitle>
                    </ItemContent>
                    <ItemActions>
                        <RadioGroupItem value="no_replace" id="alternative-no_replace" />
                    </ItemActions>
                </Item>
            </div>
            </CarouselItem>
        {alternatives.map((product, index) => (
            <CarouselItem key={index} className="basis-1/2 md:basis-1/2 lg:basis-1/2 min-w-[12rem] min-h[15rem] mx-2">
            <div className="p-1 min-w-[12rem]">
                <MissedProductAlternativeCard 
                    product={product} 
                    selected={selectedAlternative === product.hashed_sku}
                    onSelect={() => onAlternativeChange(product.hashed_sku)}
                />
            </div>
            </CarouselItem>
        ))}
        </CarouselContent>
        </RadioGroup>
        {/* <div className="flex gap-4">
            <CarouselPrevious className="left-0 top-full mt-4"/>
            <CarouselNext  className="right-0 top-full mt-4"/>
        </div> */}
    </div>
    </Carousel>
    );
});


interface MissedProductAlternativeCardProps {
    product: MinimalProductData;
    selected: boolean;
    onSelect: () => void;
}

const MissedProductAlternativeCard = forwardRef<HTMLDivElement, MissedProductAlternativeCardProps>(({product, selected, ...props},ref) =>{
    const imageUrl = product.images?.catalog??product.image_url;
    return(
        <Item 
            variant={"outline"} 
            className={`max-w-[12rem] min-w-[12rem] min-h-[15rem] cursor-pointer ${selected ? "border-2 border-primary" : "border border-muted"}`}
            onClick={props.onSelect}
        >
            <ItemHeader>         
                <img className="Image-80 mx-auto" src={imageUrl} alt={product.name}/>
            </ItemHeader>            
            <ItemContent>
                <ItemTitle className="flex flex-col gap-1 w-full items-start text-xs">
                    <span>{product.name}</span>
                    <div className="flex flex-row w-full justify-between">
                        <div className="flex flex-col gap-1">
                            <span className="text-muted-foreground">{product.total} {product.unit}</span>    
                            <Amount className="font-semibold" amount={product.price} currency={"CLP"} locale="es-CL" />                
                        </div>
                    </div>
                </ItemTitle>
            </ItemContent>
                <ItemActions>
                    <RadioGroupItem value={product.hashed_sku} id={`alternative-${product.hashed_sku}`}/>
                </ItemActions>
        </Item>
    );
});